import { createSignal, For, Show, onMount, onCleanup, createEffect, createMemo } from 'solid-js';
import { useParams } from '@solidjs/router';
import { useQuizContext } from '../QuizContext';
import QuizQuestionPreview from '../QuizQuestionPreview';
import { parseQuizMarkdown } from '../../shared/quizParser';
import socket from '../socket';
import { useUserContext } from '../UserContext';

interface LiveState {
  quizCode: string;
  quizTitle: string;
  questionIndex: number | null;
  status: 'hidden' | 'shown' | 'active' | 'stopped';
  question: any | null;
  serverNow?: number;
  receivedAt?: number;
  phaseEndsAt?: number | null;
}

export default function AdminLiveQuiz() {
  const params = useParams();
  const [quizStore] = useQuizContext() as any;
  const [userStore] = useUserContext() || (() => { throw new Error('UserContext not found'); })();

  const quiz = createMemo(() => quizStore.quizzes.find((q: any) => q.id === Number(params.id)));

  const [parsed, setParsed] = createSignal<any>(null);
  const [selectedIndex, setSelectedIndex] = createSignal<number | null>(null);
  const [liveState, setLiveState] = createSignal<LiveState | null>(null);
  const [showingResults, setShowingResults] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [now, setNow] = createSignal(Date.now());
  const [questionSeconds, setQuestionSeconds] = createSignal<number | null>(null);
  const [answerSeconds, setAnswerSeconds] = createSignal<number | null>(null);
  const [studentCopied, setStudentCopied] = createSignal(false);
  const [publicCopied, setPublicCopied] = createSignal(false);
  const secondsLeft = createMemo(() => {
    const currentState = liveState();
    if (!currentState?.phaseEndsAt) return null;
    const estimatedServerNow = currentState.serverNow && currentState.receivedAt
      ? currentState.serverNow + (now() - currentState.receivedAt)
      : now();
    return Math.max(0, Math.ceil((currentState.phaseEndsAt - estimatedServerNow) / 1000));
  });
  

  onMount(() => {
    const clock = window.setInterval(() => setNow(Date.now()), 250);
    socket.on('live-quiz-state', (s: LiveState) => {
      if (quiz() && s.quizCode === quiz().quizId) setLiveState({ ...s, receivedAt: Date.now() });
    });
    socket.on('live-quiz-update', (s: LiveState) => {
      if (quiz() && s.quizCode === quiz().quizId) setLiveState({ ...s, receivedAt: Date.now() });
    });
    socket.on('public-clear-results', () => setShowingResults(false));
    socket.on('public-question-results', () => setShowingResults(true));
    socket.on('quiz-not-available', (data: { quizId: number; state: string; message: string }) => {
      setError(data.message);
    });
    onCleanup(() => window.clearInterval(clock));
  });

  // Join only after authenticated & quizzes list loaded
  createEffect(() => {
    if (userStore.isAuthenticated && userStore.role === 'admin' && quizStore.quizzes.length > 0) {
      if (quiz()) {
        socket.emit('admin-live-join', { quizId: quiz().id });
        const p = parseQuizMarkdown(quiz().markdown);
        setParsed(p);
      }
    }
  });

  const doAction = (action: 'show'|'activate'|'stop'|'hide') => {
    if (!quiz()) return;
    const timers = { questionSeconds: questionSeconds(), answerSeconds: answerSeconds() };
    if (action === 'show') {
      socket.emit('admin-live-action', {
        quizId: quiz().id,
        action: 'show',
        questionIndex: selectedIndex(),
        timers
      });
    } else {
      socket.emit('admin-live-action', {
        quizId: quiz().id,
        action,
        questionIndex: liveState()?.questionIndex ?? selectedIndex(),
        timers
      });
    }
  };

  const showResults = () => {
    if (!quiz()) return;
    socket.emit('admin-show-results', { quizId: quiz().id });
    setShowingResults(true);
  };

  const hideResults = () => {
    if (!quiz()) return;
    socket.emit('admin-hide-results', { quizId: quiz().id });
    setShowingResults(false);
  };

  const toggleResults = () => {
    if (showingResults()) {
      hideResults();
    } else {
      showResults();
    }
  };

  const studentUrl = createMemo(() => {
    if (!quiz()) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/q/${quiz().quizId}`;
  });

  const publicUrl = createMemo(() => {
    if (!quiz()) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/public/${quiz().quizId}`;
  });

  const copyStudentUrl = () => {
    const url = studentUrl();
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setStudentCopied(true);
      setTimeout(() => setStudentCopied(false), 2000);
    }).catch(() => {
      alert('Student URL: ' + url);
    });
  };

  const copyPublicUrl = () => {
    const url = publicUrl();
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setPublicCopied(true);
      setTimeout(() => setPublicCopied(false), 2000);
    }).catch(() => {
      alert('Public URL: ' + url);
    });
  };


  return (
    <Show when={userStore.isAuthenticated && userStore.role === 'admin'} fallback={<div class="container-fluid py-3">
        <div class="alert alert-info">
          <div class="spinner-border spinner-border-sm me-2" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <Show when={userStore.isAuthenticating}>Authenticating...</Show>
          <Show when={!userStore.isAuthenticated && !userStore.isAuthenticating}>You must be logged in to access this page.</Show>
          <Show when={userStore.isAuthenticated && userStore.role !== 'admin'}>You must be an admin to access this page.</Show>
        </div>
      </div>} >
    <div class="container-fluid py-3">
      <Show when={error()}>
        <div class="alert alert-danger alert-dismissible">
          <strong>Error:</strong> {error()}
          <button type="button" class="btn-close" onClick={() => setError(null)}></button>
          <div class="mt-3">
            <a href="/manage-quizzes" class="btn btn-primary">Return to Quiz List</a>
          </div>
        </div>
      </Show>
      
      <Show when={!error() && quiz()} fallback={<div class="alert alert-info">{!quiz() ? 'Loading quizzes...' : 'Joining live session...'}</div>}>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h2>Live Control: {quiz().title}</h2>
              <div>
                <Show when={liveState()}>
                  <span class="badge bg-secondary me-2">Status: {liveState()!.status}</span>
                  <Show when={liveState()!.questionIndex != null}>
                    <span class="badge bg-info me-2">Question #{(liveState()!.questionIndex!+1)}</span>
                  </Show>
                  <Show when={secondsLeft() !== null}>
                    <span class="badge bg-primary me-2">{secondsLeft()} s</span>
                  </Show>
                </Show>
              </div>
            </div>

            {/* URLs Section */}
            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <div class="card border-primary h-100 shadow-sm">
                  <div class="card-body d-flex flex-column justify-content-between">
                    <div>
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <strong class="text-primary">Student Link (give to students)</strong>
                        <span class="badge bg-primary font-monospace fs-6">{quiz().quizId}</span>
                      </div>
                      <div class="mb-2">
                        <a href={studentUrl()} target="_blank" rel="noopener noreferrer" class="font-monospace small text-break">
                          {studentUrl()}
                        </a>
                      </div>
                      <div class="small text-muted">
                        Share this link with students to join and answer questions.
                      </div>
                    </div>
                    <div class="mt-3 d-flex gap-2">
                      <button
                        type="button"
                        class={`btn btn-sm ${studentCopied() ? 'btn-success' : 'btn-primary'}`}
                        onClick={copyStudentUrl}
                      >
                        {studentCopied() ? '✓ Copied' : 'Copy Student Link'}
                      </button>
                      <a
                        href={studentUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="btn btn-sm btn-outline-primary"
                      >
                        Open
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="card border-info h-100 shadow-sm">
                  <div class="card-body d-flex flex-column justify-content-between">
                    <div>
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <strong>Public View URL</strong>
                        <span class="badge bg-secondary font-monospace">Slides / Projector</span>
                      </div>
                      <div class="mb-2">
                        <a href={publicUrl()} target="_blank" rel="noopener noreferrer" class="font-monospace small text-break">
                          {publicUrl()}
                        </a>
                      </div>
                      <div class="small text-muted">
                        This URL can be embedded in presentation slides as an iframe.
                      </div>
                    </div>
                    <div class="mt-3 d-flex gap-2">
                      <button
                        type="button"
                        class={`btn btn-sm ${publicCopied() ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={copyPublicUrl}
                      >
                        {publicCopied() ? '✓ Copied' : 'Copy Public URL'}
                      </button>
                      <a
                        href={publicUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="btn btn-sm btn-outline-secondary"
                      >
                        Open
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-4">
                <h5 class="mb-3">Questions</h5>
                <Show when={parsed()} fallback={<div>Parsing quiz...</div>}>
                  <ul class="list-group mb-3">
                    <For each={parsed().questions}>{(q: any, i: any) => {
                      const isCurrent = () => liveState() && liveState()!.questionIndex === i();
                      return (
                        <li
                          class={`list-group-item d-flex justify-content-between align-items-center ${selectedIndex() === i() ? 'active' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => setSelectedIndex(i())}
                        >
                          <div class="d-flex align-items-center gap-2">
                            <span>#{i()+1}</span>
                            <Show when={isCurrent()}>
                              <span class="badge bg-info">Shown</span>
                            </Show>
                            <Show when={isCurrent() && liveState()!.status === 'active'}>
                              <span class="badge bg-success">Active</span>
                            </Show>
                            <Show when={isCurrent() && liveState()!.status === 'stopped'}>
                              <span class="badge bg-warning text-dark">Stopped</span>
                            </Show>
                          </div>
                          <span class="small text-muted">{q.isMultipleChoice ? 'Multiple' : 'Single'}</span>
                        </li>
                      );
                    }}</For>
                  </ul>
                  <div class="card card-body p-2 mb-3">
                    <div class="small fw-semibold mb-2">Timers for this live session</div>
                    <div class="row g-2">
                      <div class="col-6">
                        <label class="form-label small mb-1" for="live-question-seconds">Question</label>
                        <div class="input-group input-group-sm">
                          <input id="live-question-seconds" type="number" inputmode="numeric"
                            class="form-control" min="1" max="3600" placeholder="Manual"
                            value={questionSeconds() ?? ''}
                            onInput={(e) => setQuestionSeconds(e.currentTarget.value === '' ? null : Number(e.currentTarget.value))} />
                          <span class="input-group-text">s</span>
                        </div>
                      </div>
                      <div class="col-6">
                        <label class="form-label small mb-1" for="live-answer-seconds">Answers</label>
                        <div class="input-group input-group-sm">
                          <input id="live-answer-seconds" type="number" inputmode="numeric"
                            class="form-control" min="1" max="3600" placeholder="Manual"
                            value={answerSeconds() ?? ''}
                            onInput={(e) => setAnswerSeconds(e.currentTarget.value === '' ? null : Number(e.currentTarget.value))} />
                          <span class="input-group-text">s</span>
                        </div>
                      </div>
                    </div>
                    <div class="form-text mt-1">Empty means manual. Changes apply to the next action.</div>
                  </div>
                  <div class="d-flex flex-column gap-2 mb-3">
                    {/* Main question control */}
                    <Show when={!liveState() || liveState()!.status === 'hidden'} fallback={
                      <div class="d-grid gap-2" role="group">
                        <Show when={liveState()!.status === 'shown'}>
                          <button class="btn btn-primary" onClick={() => doAction('activate')}>Show choices</button>
                        </Show>
                        <button 
                          class="btn btn-outline-warning" 
                          disabled={!liveState() || liveState()!.status !== 'active'} 
                          onClick={() => doAction('stop')}
                        >
                          Stop Answers
                        </button>
                        <button 
                          class={`btn ${showingResults() ? 'btn-success' : 'btn-outline-success'}`}
                          disabled={!liveState() || liveState()!.questionIndex === null}
                          onClick={toggleResults}
                        >
                          {showingResults() ? '✓ Results Shown' : 'Show Results'}
                        </button>
                        <button class="btn btn-outline-secondary" onClick={() => doAction('hide')}>
                          Hide Question
                        </button>
                      </div>
                    }>
                      <button 
                        class="btn btn-primary btn-lg" 
                        disabled={selectedIndex() == null} 
                        onClick={() => doAction('show')}
                      >
                        Show Question
                      </button>
                    </Show>
                  </div>
                </Show>
              </div>
              <div class="col-md-8">
                <h5 class="mb-3">Preview</h5>
                <Show when={selectedIndex()!=null} fallback={<div class="alert alert-info">Select a question to preview.</div>}>
                  <QuizQuestionPreview question={parsed().questions[selectedIndex()!]} readonly />
                </Show>
                <Show when={liveState() && liveState()!.question && liveState()!.questionIndex!=null}>
                  <div class="mt-4">
                    <h6>Currently Shown to Students</h6>
                    <QuizQuestionPreview question={liveState()!.question} readonly={liveState()!.status!=='active'} />
                  </div>
                </Show>
              </div>
            </div>
          </Show>
        </div>
      </Show>
  );
}
